/**
 * `DataTableSummaryBand` — the preset's pinned-bottom aggregate band: one engine row rendered
 * through the SAME visible leaf columns as the body, wrapped in the sticky `TableRow` band.
 * Split out of `data-table.tsx` for the same reason as `DataTableHeaderRow` — the orchestrator
 * holds the band as a single node instead of a second cell-derivation block.
 *
 * REACTIVITY: the cell tracks (width / grow flex / pinned offset / alignment) are derived HERE,
 * in this component's own render — through the same `getCellTrackProps` the body rows use — off
 * the referentially-stable `table`, reads the React Compiler cannot see change. `pinnedOffsets`
 * is the carrier that keeps them live: the preset MEMOIZES that map on exactly the inputs the
 * pinned geometry derives from (`PinnedGeometryMemoKey` — the four state slices plus the
 * column defs), so a new identity arrives precisely when a resize / visibility / order / pin
 * change lands, and NOT on unrelated renders. Same mechanism `DataTableHeaderRow` relies on;
 * anything added to that geometry must join the key, or these tracks freeze.
 */
import type { CSSProperties } from 'react'
import { flexRender, type Row, type RowData } from '@tanstack/react-table'

import { cn } from '@gto/ui/cn'

import type { DataTableSummaryRowProps } from '../data-table.types'
import { readSummaryCell } from './column-meta'
import { getCellTrackProps } from './column-size'
import {
  type DataTableDensity,
  dataTableSummaryBandVariants,
  type DataTableSurface,
} from './data-table.styles'
import type { PinnedOffsetMap } from './pinning.styles'
import { SELECTION_COLUMN_WIDTH_PX, TableCell, TableRow } from './table-parts'

export type DataTableSummaryBandProps<TData extends RowData> = {
  /**
   * The aggregate row, built with TanStack's `createRow` against the live table — so its
   * `getVisibleCells()` honours the current visibility / order / pinning, and each cell's
   * context feeds the column def's own `cell` renderer. It is NOT part of the engine's row
   * model, which is what keeps every count blind to it.
   */
  row: Row<TData>
  density: DataTableDensity
  /** The band's elevation — opaque, so body rows scrolling under it stay hidden. */
  surface: DataTableSurface
  pinnedOffsets: PinnedOffsetMap
  /** Whether the body uses grow columns, which binds every plane to the scrollport. */
  fitScrollport: boolean
  /**
   * A `selection` model renders a model-external leading column, so the band opens with an
   * EMPTY cell of the same width (no checkbox — an aggregate is not selectable) and every
   * data cell's `aria-colindex` shifts by one, keeping the column tracks aligned.
   */
  hasSelectionColumn: boolean
  /** Sticky style for the leading cell when a data column is left-pinned; from `useSelectionColumn`. */
  selectionPinnedStyle?: CSSProperties
  /**
   * Opaque surface class for that same leading cell, from `useSelectionColumn`'s HEADER
   * variant: the band and the header row are the two chrome bands and share `headerSurface`,
   * so one pinned-surface derivation serves both (see `pinnedCellSurfaceClass`).
   */
  selectionPinnedClassName?: string
  /** Consumer props merged onto the band (test hooks, `aria-label`, …). */
  rowProps?: DataTableSummaryRowProps
  /**
   * The band's distance from the scrollport's bottom edge, in px — the height of the bands
   * stacked under it, `0` for the lowest. Inline rather than a variant because it is geometry
   * the stack derives per band; it overrides the variant's `bottom-0`.
   *
   * One shared sticky wrapper around the bands would remove this prop and the inline style
   * with it, and review argued it should. It is NOT ruled out — it was left alone because it
   * nests the `role="row"` bands one level deeper inside `role="grid"`, and nobody has yet
   * checked a screen reader or a real browser against that. Take the wrapper when someone
   * can verify it; the ratchet carries a `libs/ui.inlineStyles` exception until they do.
   */
  bottomPx?: number
}

/**
 * Plain generic function (not `FC`) so `Row<TData>` inference survives — same reason as the
 * header row / menu components.
 */
export const DataTableSummaryBand = <TData extends RowData>({
  row,
  density,
  surface,
  pinnedOffsets,
  fitScrollport,
  hasSelectionColumn,
  selectionPinnedStyle,
  selectionPinnedClassName,
  rowProps,
  bottomPx = 0,
}: DataTableSummaryBandProps<TData>) => {
  const { className: consumerClassName, style: consumerStyle, ...restRowProps } = rowProps ?? {}
  const dataColOffset = hasSelectionColumn ? 1 : 0

  return (
    <TableRow
      data-summary-row
      // `divider` (bordered, square, contiguous) rather than the floating row model: a band
      // spanning the card's full width has no business rounding its corners mid-scroll.
      divider
      className={cn(dataTableSummaryBandVariants({ surface }), consumerClassName)}
      density={density}
      fitScrollport={fitScrollport}
      // A chrome band, not a data row — never hover-tints (DataGridPro footer parity).
      hoverTint={false}
      // `TableRow` emits `role="row"` only alongside an `aria-rowindex`; the band carries the
      // role WITHOUT the index on purpose — see `DataTableSummaryRow` in `data-table.types.ts`.
      role="row"
      // The stack offset first, so a consumer's own `style` still composes over it.
      style={{ ...(bottomPx === 0 ? {} : { bottom: bottomPx }), ...consumerStyle }}
      {...restRowProps}
    >
      {hasSelectionColumn && (
        <TableCell
          align="center"
          className={selectionPinnedClassName}
          colIndex={1}
          style={{ width: SELECTION_COLUMN_WIDTH_PX, ...selectionPinnedStyle }}
        />
      )}
      {row.getVisibleCells().map((cell, colIndex) => (
        <TableCell
          key={cell.id}
          colIndex={colIndex + 1 + dataColOffset}
          {...getCellTrackProps(cell, pinnedOffsets, surface)}
        >
          {/*
            `meta.summaryCell` when the column formats its total differently, else the
            column's own `cell` — the same context either way, so a column needs no
            "am I the summary row?" branch inside one renderer.
          */}
          {flexRender(readSummaryCell(cell) ?? cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  )
}
